import * as THREE from 'three';
import { ASSET_CATALOG, type AssetName } from './catalog';

/** Primitive stand-ins with the SAME node names, pivot and markup as the Blender assets. */
function part(asset: AssetName, name: string, geometry: THREE.BufferGeometry, centerY: number, material: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.position.y = centerY;
  mesh.userData = { ...(ASSET_CATALOG[asset].nodes as Record<string, object>)[name] };
  return mesh;
}

export function toroStub(): THREE.Object3D {
  const stone = new THREE.MeshStandardMaterial({ color: 0x66665f, roughness: 0.9 });
  const paper = new THREE.MeshStandardMaterial({ color: 0xebdcb3, roughness: 0.9 });
  const root = new THREE.Group();
  root.name = 'toro';
  root.add(
    part('toro', 'toro_base', new THREE.CylinderGeometry(0.36, 0.42, 0.14, 6), 0.07, stone),
    part('toro', 'toro_post', new THREE.CylinderGeometry(0.1, 0.1, 0.7, 12), 0.49, stone),
    part('toro', 'toro_platform', new THREE.CylinderGeometry(0.34, 0.24, 0.14, 6), 0.91, stone),
    part('toro', 'toro_firebox', new THREE.CylinderGeometry(0.3, 0.3, 0.38, 6, 1, true), 1.17, stone),
    part('toro', 'toro_light', new THREE.CylinderGeometry(0.23, 0.23, 0.32, 6), 1.17, paper),
    part('toro', 'toro_roof', new THREE.CylinderGeometry(0.07, 0.52, 0.3, 6), 1.51, stone),
    part('toro', 'toro_jewel', new THREE.ConeGeometry(0.07, 0.14, 8), 1.73, stone),
  );
  return root;
}

/**
 * Minimal stand-in for any catalog asset: one small grey box per declared node, carrying that node's markup.
 * Only shown if the GLB fails to load; keeps names/markup identical so logic never sees a difference.
 */
export function catalogStub(name: AssetName): THREE.Object3D {
  const material = new THREE.MeshStandardMaterial({ color: 0x77766f, roughness: 0.9 });
  const root = new THREE.Group();
  root.name = name;
  const nodes = Object.keys(ASSET_CATALOG[name].nodes);
  nodes.forEach((node, i) => root.add(part(name, node, new THREE.BoxGeometry(0.3, 0.3, 0.3), 0.15 + 0.3 * i, material)));
  return root;
}

export const STUBS = Object.fromEntries(
  (Object.keys(ASSET_CATALOG) as AssetName[]).map((name) => [name, name === 'toro' ? toroStub : () => catalogStub(name)]),
) as Record<AssetName, () => THREE.Object3D>;

const MARKUP_KEYS = ['snow', 'wet', 'emissive', 'wind'] as const;

/** Node name -> markup for every descendant (the root excluded). Used to compare a GLB against its stub. */
export function interfaceOf(root: THREE.Object3D): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  root.traverse((o) => {
    if (o === root || !o.name) return;
    const markup: Record<string, unknown> = {};
    for (const k of MARKUP_KEYS) if (o.userData[k] !== undefined) markup[k] = o.userData[k];
    out[o.name] = markup;
  });
  return out;
}
